export type TemperatureUnit = "fahrenheit" | "celsius"
export type ThermostatHvacMode = "heat" | "cool" | "heat_cool" | "off"
export type ThermostatFanMode = "auto" | "on" | "circulate"
export type ThermostatSetPoint = "heating" | "cooling"

/** Both targets are retained when changing modes; Celsius is the canonical unit. */
export type ThermostatClimateSetting = {
  hvac_mode_setting: ThermostatHvacMode
  fan_mode_setting: ThermostatFanMode
  heating_set_point_celsius: number
  cooling_set_point_celsius: number
}

export type ThermostatSetPointLimits = {
  minCelsius: number
  maxCelsius: number
  minHeatCoolDeltaCelsius: number
  minHeatingCelsius?: number
  maxHeatingCelsius?: number
  minCoolingCelsius?: number
  maxCoolingCelsius?: number
}

/** Workbench example values, not a Honeywell capability specification. */
export const DEFAULT_THERMOSTAT_LIMITS: ThermostatSetPointLimits = {
  minCelsius: 10,
  maxCelsius: 32.2222222222,
  minHeatCoolDeltaCelsius: 2,
}

const EPSILON = 1e-9

export function celsiusToDisplay(value: number, unit: TemperatureUnit): number {
  return unit === "fahrenheit" ? value * 9 / 5 + 32 : value
}

export function displayToCelsius(value: number, unit: TemperatureUnit): number {
  return unit === "fahrenheit" ? (value - 32) * 5 / 9 : value
}

/** Numeric display text only: whole degrees Fahrenheit or half degrees Celsius. */
export function formatTemperature(valueCelsius: number, unit: TemperatureUnit): string {
  if (!Number.isFinite(valueCelsius)) {
    return "—"
  }
  const value = celsiusToDisplay(valueCelsius, unit)
  return String(unit === "fahrenheit" ? Math.round(value) : Math.round(value * 2) / 2)
}

function resolveLimits(limits: ThermostatSetPointLimits) {
  const { minCelsius, maxCelsius, minHeatCoolDeltaCelsius } = limits
  const heating = {
    minCelsius: limits.minHeatingCelsius ?? minCelsius,
    maxCelsius: limits.maxHeatingCelsius ?? maxCelsius,
  }
  const cooling = {
    minCelsius: limits.minCoolingCelsius ?? minCelsius,
    maxCelsius: limits.maxCoolingCelsius ?? maxCelsius,
  }
  if (
    ![minCelsius, maxCelsius, minHeatCoolDeltaCelsius, ...Object.values(heating), ...Object.values(cooling)]
      .every(Number.isFinite)
      || minCelsius > maxCelsius
      || heating.minCelsius > heating.maxCelsius
      || cooling.minCelsius > cooling.maxCelsius
      || minHeatCoolDeltaCelsius < 0
      || minHeatCoolDeltaCelsius > cooling.maxCelsius - heating.minCelsius
  ) {
    throw new RangeError("Thermostat limits must be finite and fit the minimum heat/cool gap.")
  }
  return { heating, cooling }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/**
 * Auto maintains at least the configured gap and leaves the opposite target fixed.
 * A device adapter can supply a stricter gap if its provider requires one.
 */
export function getThermostatSetPointBounds(
  setting: ThermostatClimateSetting,
  target: ThermostatSetPoint,
  limits: ThermostatSetPointLimits = DEFAULT_THERMOSTAT_LIMITS,
): { minCelsius: number, maxCelsius: number } {
  const ranges = resolveLimits(limits)
  let { minCelsius, maxCelsius } = ranges[target]
  if (setting.hvac_mode_setting === "heat_cool") {
    if (target === "heating") {
      maxCelsius = Math.min(maxCelsius, setting.cooling_set_point_celsius - limits.minHeatCoolDeltaCelsius)
    }
    else {
      minCelsius = Math.max(minCelsius, setting.heating_set_point_celsius + limits.minHeatCoolDeltaCelsius)
    }
  }
  return { minCelsius, maxCelsius }
}

export function adjustThermostatSetPoint(
  setting: ThermostatClimateSetting,
  target: ThermostatSetPoint,
  delta: -1 | 1,
  unit: TemperatureUnit,
  limits: ThermostatSetPointLimits = DEFAULT_THERMOSTAT_LIMITS,
): ThermostatClimateSetting {
  const mode = setting.hvac_mode_setting
  if (mode === "off" || (mode === "heat" && target !== "heating") || (mode === "cool" && target !== "cooling")) {
    return setting
  }

  const field = target === "heating" ? "heating_set_point_celsius" : "cooling_set_point_celsius"
  const current = setting[field]
  const { minCelsius, maxCelsius } = getThermostatSetPointBounds(setting, target, limits)
  if (![current, minCelsius, maxCelsius].every(Number.isFinite) || minCelsius > maxCelsius) {
    return setting
  }

  // Convert only for the increment: a 1°F press must not become a rounded 0.5°C press.
  const step = unit === "fahrenheit" ? 1 : 0.5
  // Round the bounds inward so a fractional constraint cannot cause an invisible
  // press (72°F -> 72.4°F) or move in the opposite direction to the pressed key.
  const minDisplay = Math.ceil((celsiusToDisplay(minCelsius, unit) - EPSILON) / step) * step
  const maxDisplay = Math.floor((celsiusToDisplay(maxCelsius, unit) + EPSILON) / step) * step
  if (minDisplay > maxDisplay) {
    return setting
  }
  const candidate = celsiusToDisplay(current, unit) + delta * step
  const next = clamp(displayToCelsius(clamp(candidate, minDisplay, maxDisplay), unit), minCelsius, maxCelsius)
  if (delta * (next - current) < EPSILON || formatTemperature(next, unit) === formatTemperature(current, unit)) {
    return setting
  }
  return { ...setting, [field]: next }
}

export function setThermostatHvacMode(
  setting: ThermostatClimateSetting,
  mode: ThermostatHvacMode,
  limits: ThermostatSetPointLimits = DEFAULT_THERMOSTAT_LIMITS,
): ThermostatClimateSetting {
  const ranges = resolveLimits(limits)
  const { minHeatCoolDeltaCelsius } = limits
  let heating = Number.isFinite(setting.heating_set_point_celsius)
    ? clamp(setting.heating_set_point_celsius, ranges.heating.minCelsius, ranges.heating.maxCelsius)
    : ranges.heating.minCelsius
  let cooling = Number.isFinite(setting.cooling_set_point_celsius)
    ? clamp(setting.cooling_set_point_celsius, ranges.cooling.minCelsius, ranges.cooling.maxCelsius)
    : ranges.cooling.maxCelsius

  if (mode === "heat_cool" && cooling - heating < minHeatCoolDeltaCelsius) {
    cooling = Math.min(ranges.cooling.maxCelsius, heating + minHeatCoolDeltaCelsius)
    heating = Math.min(heating, cooling - minHeatCoolDeltaCelsius)
  }

  return {
    ...setting,
    hvac_mode_setting: mode,
    heating_set_point_celsius: heating,
    cooling_set_point_celsius: cooling,
  }
}
